import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import styled from "styled-components";
import {
  BsFillHandThumbsUpFill,
  BsFillHandThumbsDownFill,
  BsBanFill,
} from "react-icons/bs";
import { TbZoomFilled } from "react-icons/tb";
import { FaHourglass, FaUserSlash } from "react-icons/fa";
import { Input, Modal, Tooltip, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useNavContext } from "../context/nav_context";
import {
  PENDING, REV_IN_PROGRESS, APPROBED, REJECTED, BANNED,
  api_v3_update_player_admin, api_v3_update_player_tournament_status_admin,
} from "../utils/constants";
import { TournamentHistory, Aoe2InfoPopup, ClanSelector } from "./";
import UserFormFields from "./form/UserFormFields";

const STATUS_CONFIG = {
  [PENDING]: { Icon: FaHourglass, title: "Pendiente revisar", color: "#ff9f43" },
  [REV_IN_PROGRESS]: { Icon: TbZoomFilled, title: "Revisión en curso", color: "#3860be" },
  [APPROBED]: { Icon: BsFillHandThumbsUpFill, title: "Aceptado", color: "var(--v-mint)" },
  [REJECTED]: { Icon: BsFillHandThumbsDownFill, title: "Rechazado", color: "var(--v-uv)" },
  [BANNED]: { Icon: BsBanFill, title: "Baneado", color: "#c9a04e" },
};

const AOE2_RESOURCES = [
  { url: "https://aoe2.net/#aoe2de-profile-", icon: "aoe2net.png", alt: "AoE2 Net" },
  { url: "https://smurf.new-chapter.eu/check_player?player_id=", icon: "smurf.svg", alt: "Smurf database" },
  { url: "https://www.aoe2insights.com/user/", icon: "aoe2insights.webp", alt: "AoE2 Insights" },
  { url: "https://aoe2.gg/profile/", icon: "aoe2_gg.svg", alt: "AoE2.gg" },
];

const RegistrationsItem = ({
  registration, tournamentId, clans, clansInProgress, clansError,
  tournaments, loadingTournaments, errorTournaments,
}) => {
  const { accessToken, user } = useNavContext();
  const [uiState, setUiState] = useState({ openPopup: false, showClanSelector: false, showAvatarEdit: false });
  const [saveState, setSaveState] = useState({ showButton: false, inProgress: false, done: false, error: false });
  const [selectedClanId, setSelectedClanId] = useState(registration.clanId || -1);

  const selectedClan = useMemo(() => {
    if (!clans || !Array.isArray(clans) || selectedClanId === -1) return { name: "Ninguno", image: "" };
    const clan = clans.find((c) => c.clanId === selectedClanId);
    return clan ? { name: clan.name || "Sin nombre", image: clan.image || "" } : { name: "Ninguno", image: "" };
  }, [clans, selectedClanId]);

  const [editedRegistration, setEditedRegistration] = useState({ ...registration });
  const [tournamentState, setTournamentState] = useState({ status: "", comments: "" });
  const [commentState, setCommentState] = useState({ newComment: "", pendingComment: null });

  useEffect(() => {
    if (selectedClanId !== (registration.clanId || -1)) {
      setEditedRegistration((prev) => ({ ...prev, clanId: selectedClanId }));
      setSaveState((prev) => ({ ...prev, showButton: true }));
    }
  }, [selectedClanId, registration.clanId]);

  useEffect(() => {
    const tournament = registration.tournamentHistory.find((t) => t.tournamentId === tournamentId);
    if (tournament) setTournamentState({ status: tournament.status || "", comments: tournament.comments || "" });
  }, [registration.tournamentHistory, tournamentId]);

  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    setSaveState((prev) => ({ ...prev, showButton: true }));
    setEditedRegistration((prev) => ({ ...prev, [name]: value }));
  };

  const handleRegStatusChange = (status) => {
    if (tournamentState.status) {
      setSaveState((prev) => ({ ...prev, showButton: true }));
      setTournamentState((prev) => ({ ...prev, status }));
    }
  };

  const handleCommentChange = (e) => {
    const { value } = e.target;
    setSaveState((prev) => ({ ...prev, showButton: true }));
    setTournamentState((prev) => ({ ...prev, comments: value }));
  };

  const saveRegister = async () => {
    setSaveState((prev) => ({ ...prev, inProgress: true, error: false }));
    try {
      const new_reg_info = { ...editedRegistration, clanId: selectedClanId };
      const tournamentIndex = new_reg_info.tournamentHistory.findIndex((t) => t.tournamentId === tournamentId);
      const currentTournament = new_reg_info.tournamentHistory[tournamentIndex];
      const existingComments = currentTournament.forumComments || [];
      const isDuplicateComment = commentState.pendingComment && existingComments.some((c) => c.date === commentState.pendingComment.date);
      const tournamentInfo = {
        ...currentTournament,
        status: tournamentState.status,
        comments: tournamentState.comments,
        forumComments: [...existingComments, ...(commentState.pendingComment && !isDuplicateComment ? [commentState.pendingComment] : [])],
      };
      if (tournamentIndex !== -1) new_reg_info.tournamentHistory[tournamentIndex] = tournamentInfo;
      const access_token = accessToken || localStorage.getItem("access_token");
      if (!access_token) return false;
      const config = { headers: { Authorization: `Bearer ${access_token}` } };
      const [response, response2] = await Promise.all([
        axios.put(api_v3_update_player_admin(new_reg_info.playerId), new_reg_info, config),
        axios.put(api_v3_update_player_tournament_status_admin(new_reg_info.playerId, tournamentId), tournamentInfo, config),
      ]);
      if (response.status === 200 && response2.status === 200) {
        setEditedRegistration((prev) => {
          const updatedHistory = [...prev.tournamentHistory];
          const idx = updatedHistory.findIndex((t) => t.tournamentId === tournamentId);
          if (idx !== -1) updatedHistory[idx] = tournamentInfo;
          return { ...prev, tournamentHistory: updatedHistory };
        });
        setSaveState({ showButton: false, inProgress: false, done: true, error: false });
        setCommentState((prev) => ({ ...prev, pendingComment: null }));
        setTimeout(() => setSaveState({ showButton: false, inProgress: false, done: false, error: false }), 2000);
        return true;
      }
      setSaveState((prev) => ({ ...prev, inProgress: false, error: true }));
      return false;
    } catch (error) {
      console.error("Save failed:", error);
      setSaveState((prev) => ({ ...prev, inProgress: false, error: true }));
      return false;
    }
  };

  return (
    <Card>
      <CardHeader>
        <AvatarWrap onClick={() => setUiState((prev) => ({ ...prev, showAvatarEdit: true }))}>
          {editedRegistration.discordAvatarURL ? (
            <AvatarImg src={editedRegistration.discordAvatarURL} alt={editedRegistration.discordUsername} />
          ) : (
            <AvatarPlaceholder><FaUserSlash /></AvatarPlaceholder>
          )}
        </AvatarWrap>
        <CardTitle>
          <PlayerName>{editedRegistration.username}</PlayerName>
          <PlayerId>ID: {editedRegistration.playerId}</PlayerId>
        </CardTitle>
        <ClanWrap onClick={() => setUiState((prev) => ({ ...prev, showClanSelector: true }))}>
          {selectedClan.image ? (
            <ClanImg src={selectedClan.image} alt={selectedClan.name} />
          ) : (
            <ClanPlaceholder>?</ClanPlaceholder>
          )}
          <ClanName>{selectedClan.name}</ClanName>
        </ClanWrap>
      </CardHeader>

      <MainGrid>
        <SideCol>
          <CopyBtn onClick={() => { navigator.clipboard.writeText(editedRegistration.playerId); message.success("ID copiado"); }}>
            COPIAR ID
          </CopyBtn>
          <CopyBtn onClick={() => { navigator.clipboard.writeText(editedRegistration.discordUsername); message.success("Discord copiado"); }}>
            DISCORD: {editedRegistration.discordUsername}
          </CopyBtn>
        </SideCol>
        <FormCol>
          <UserFormFields
            style={{ width: "100%" }}
            editedRegistration={editedRegistration}
            onInputChange={handleChangeForm}
            AOE2_RESOURCES={AOE2_RESOURCES}
            setOpenPopup={(open) => setUiState((prev) => ({ ...prev, openPopup: open }))}
          />
        </FormCol>
      </MainGrid>

      <Section>
        <SectionTitle>HISTORIAL TORNEOS</SectionTitle>
        {loadingTournaments ? (
          <LoadingDots />
        ) : errorTournaments ? (
          <MutedText>Error cargando los torneos</MutedText>
        ) : (
          <TournamentHistory tournamentHistory={editedRegistration.tournamentHistory} tournaments={tournaments || []} />
        )}
      </Section>

      {tournamentState.status ? (
        <Section>
          <SectionTitle>COMENTARIOS</SectionTitle>
          <CommentInput
            placeholder="Escribe un comentario..."
            value={commentState.newComment}
            onChange={(e) => setCommentState((prev) => ({ ...prev, newComment: e.target.value }))}
          />
          <CommentBtn
            onClick={() => {
              if (!user) { message.error("Debes iniciar sesión para comentar"); return; }
              if (commentState.newComment.trim()) {
                setSaveState((prev) => ({ ...prev, showButton: true }));
                setCommentState((prev) => ({
                  newComment: "",
                  pendingComment: {
                    userId: user.playerId,
                    userName: user.username || user.discordUsername,
                    date: new Date().toISOString(),
                    text: prev.newComment.trim(),
                  },
                }));
              }
            }}
            disabled={!commentState.newComment.trim()}
          >
            COMENTAR
          </CommentBtn>

          <CommentList>
            {[
              ...(editedRegistration.tournamentHistory.find((t) => t.tournamentId === tournamentId)?.forumComments || []),
              ...(commentState.pendingComment ? [{ ...commentState.pendingComment, isPending: true }] : []),
            ].map((comment, index) => (
              <CommentCard key={index} $pending={comment.isPending}>
                <CommentHeader>
                  <CommentAuthor>{comment.userName}</CommentAuthor>
                  {comment.isPending && <PendingBadge>Pendiente</PendingBadge>}
                  <CommentDate>{new Date(comment.date).toLocaleString()}</CommentDate>
                  <DeleteBtn onClick={() => {
                    const currentHistory = [...editedRegistration.tournamentHistory];
                    const idx = currentHistory.findIndex((t) => t.tournamentId === tournamentId);
                    if (idx === -1) return;
                    currentHistory[idx].forumComments = (currentHistory[idx].forumComments || []).filter((_, i) => i !== index);
                    setEditedRegistration((prev) => ({ ...prev, tournamentHistory: currentHistory }));
                    setSaveState((prev) => ({ ...prev, showButton: true }));
                    message.success("Comentario eliminado");
                  }}><DeleteOutlined /></DeleteBtn>
                </CommentHeader>
                <CommentText>{comment.text}</CommentText>
              </CommentCard>
            ))}
          </CommentList>

          {tournamentState.comments !== undefined && (
            <AdminCommentInput
              placeholder="Comentarios del administrador"
              value={tournamentState.comments}
              onChange={handleCommentChange}
            />
          )}
        </Section>
      ) : (
        <MutedText style={{ margin: "1rem 0" }}>El jugador no está registrado en el torneo</MutedText>
      )}

      <ActionsRow>
        {saveState.showButton && (
          <SaveBtn onClick={saveRegister} $loading={saveState.inProgress} $error={saveState.error} $done={saveState.done}>
            {saveState.inProgress ? "GUARDANDO..." : saveState.error ? "ERROR! REINTENTAR" : saveState.done ? "GUARDADO" : "GUARDAR CAMBIOS"}
          </SaveBtn>
        )}
        <StatusGroup>
          {Object.entries(STATUS_CONFIG).map(([status, { Icon, title, color }]) => {
            const isActive = tournamentState.status === status;
            return (
              <Tooltip key={status} title={title}>
                <StatusBtn
                  $active={isActive}
                  $color={color}
                  onClick={() => handleRegStatusChange(status)}
                  disabled={!tournamentState.status}
                >
                  <Icon />
                </StatusBtn>
              </Tooltip>
            );
          })}
        </StatusGroup>
      </ActionsRow>

      {/* AoE2 Info Popup Modal */}
      <Modal
        open={uiState.openPopup}
        onCancel={() => setUiState((prev) => ({ ...prev, openPopup: false }))}
        footer={null}
        width="90vw"
        styles={{
          body: { background: "#141414", padding: "24px", color: "#fff" },
          header: { background: "#141414", borderBottom: "1px solid #303030" },
          mask: { backgroundColor: "rgba(0,0,0,0.85)" },
          content: { background: "#141414", borderRadius: "8px", boxShadow: "0 0 8px rgba(0,0,0,0.3)" },
        }}
      >
        <Aoe2InfoPopup setOpenPopup={(open) => setUiState((prev) => ({ ...prev, openPopup: open }))} user={editedRegistration} />
      </Modal>

      {/* Clan Selector Modal */}
      <Modal
        title={`Seleccionar clan (${clans.length} clanes disponibles)`}
        open={uiState.showClanSelector}
        onCancel={() => setUiState((prev) => ({ ...prev, showClanSelector: false }))}
        footer={null}
        width={800}
        styles={{
          body: { background: "#141414", padding: "24px", color: "#fff" },
          header: { background: "#141414", borderBottom: "1px solid #303030" },
          mask: { backgroundColor: "rgba(0,0,0,0.85)" },
          content: { background: "#141414", borderRadius: "8px", boxShadow: "0 0 8px rgba(0,0,0,0.3)" },
        }}
      >
        <ClanSelector
          isOpen={true}
          onClose={() => setUiState((prev) => ({ ...prev, showClanSelector: false }))}
          clans={clans}
          selectedClanId={selectedClanId}
          onSelectClan={setSelectedClanId}
          onSave={() => { setUiState((prev) => ({ ...prev, showClanSelector: false })); setSaveState((prev) => ({ ...prev, showButton: true })); }}
          loading={clansInProgress}
          error={clansError}
        />
      </Modal>

      {/* Avatar Edit Modal */}
      <Modal
        title="Editar URL del avatar"
        open={uiState.showAvatarEdit}
        onOk={() => { if (editedRegistration.discordAvatarURL) { setUiState((prev) => ({ ...prev, showAvatarEdit: false })); setSaveState((prev) => ({ ...prev, showButton: true })); }}}
        onCancel={() => setUiState((prev) => ({ ...prev, showAvatarEdit: false }))}
        okButtonProps={{ disabled: !editedRegistration.discordAvatarURL }}
        styles={{
          body: { background: "#141414", padding: "24px", color: "#fff" },
          header: { background: "#141414", borderBottom: "1px solid #303030" },
          mask: { backgroundColor: "rgba(0,0,0,0.85)" },
          content: { background: "#141414", borderRadius: "8px", boxShadow: "0 0 8px rgba(0,0,0,0.3)" },
        }}
      >
        <Input
          placeholder="URL del avatar"
          value={editedRegistration.discordAvatarURL}
          onChange={(e) => setEditedRegistration((prev) => ({ ...prev, discordAvatarURL: e.target.value }))}
          style={{ backgroundColor: "#1f1f1f", borderColor: "#434343", color: "#fff" }}
        />
      </Modal>
    </Card>
  );
};

const Card = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 1.5rem;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const AvatarWrap = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
  cursor: pointer;
  transition: border-color 0.15s ease;
  &:hover { border-color: var(--v-mint); }
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.04);
  color: var(--v-fg-muted);
  font-size: 1.2rem;
`;

const CardTitle = styled.div`
  flex: 1;
  min-width: 0;
`;

const PlayerName = styled.div`
  font-family: var(--v-font-body);
  font-size: 18px;
  font-weight: 600;
  color: var(--v-fg);
`;

const PlayerId = styled.div`
  font-family: var(--v-font-mono);
  font-size: 10px;
  color: var(--v-fg-muted);
  margin-top: 2px;
`;

const ClanWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.8; }
`;

const ClanImg = styled.img`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  object-fit: contain;
`;

const ClanPlaceholder = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--v-font-mono);
  font-size: 14px;
  color: var(--v-fg-muted);
`;

const ClanName = styled.span`
  font-family: var(--v-font-mono);
  font-size: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: var(--v-fg-muted);
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const SideCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 140px;
`;

const CopyBtn = styled.button`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--v-fg-muted);
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, color 0.15s ease;
  line-height: 1.3;
  &:hover { border-color: var(--v-mint); color: var(--v-mint); }
`;

const FormCol = styled.div`
  min-width: 0;
`;

const Section = styled.div`
  padding-top: 1.25rem;
  margin-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
`;

const SectionTitle = styled.h4`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-fg-muted);
  margin: 0 0 0.75rem;
`;

const MutedText = styled.p`
  font-family: var(--v-font-body);
  font-size: 13px;
  color: var(--v-fg-muted);
  margin: 0;
`;

const LoadingDots = styled.div`
  &::after {
    content: "...";
    font-family: var(--v-font-mono);
    font-size: 14px;
    color: var(--v-fg-muted);
  }
`;

const CommentInput = styled.textarea`
  width: 100%;
  padding: 0.65rem 0.85rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  color: var(--v-fg);
  font-family: var(--v-font-body);
  font-size: 13px;
  outline: none;
  resize: vertical;
  min-height: 3rem;
  box-sizing: border-box;
  margin-bottom: 0.5rem;
  &:focus { border-color: var(--v-mint); }
  &::placeholder { color: var(--v-fg-muted); opacity: 0.4; }
`;

const CommentBtn = styled.button`
  background: var(--v-mint);
  border: none;
  color: #000;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.45rem 1.25rem;
  border-radius: 20px;
  cursor: pointer;
  transition: opacity 0.15s ease;
  margin-bottom: 1rem;
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 1rem;
  max-height: 300px;
  overflow-y: auto;
`;

const CommentCard = styled.div`
  background: ${({ $pending }) => ($pending ? "rgba(56, 96, 190, 0.1)" : "rgba(255,255,255,0.02)")};
  border: 1px solid ${({ $pending }) => ($pending ? "rgba(56, 96, 190, 0.2)" : "rgba(255,255,255,0.04)")};
  border-radius: 12px;
  padding: 0.75rem;
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
`;

const CommentAuthor = styled.span`
  font-family: var(--v-font-body);
  font-size: 12px;
  font-weight: 600;
  color: var(--v-hover);
`;

const PendingBadge = styled.span`
  font-family: var(--v-font-mono);
  font-size: 8px;
  font-weight: 600;
  text-transform: uppercase;
  color: #3860be;
  background: rgba(56, 96, 190, 0.15);
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
`;

const CommentDate = styled.span`
  font-family: var(--v-font-mono);
  font-size: 9px;
  color: var(--v-fg-muted);
  margin-left: auto;
`;

const DeleteBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255, 59, 59, 0.5);
  cursor: pointer;
  font-size: 13px;
  padding: 0.2rem;
  display: flex;
  &:hover { color: #ff3b3b; }
`;

const CommentText = styled.p`
  font-family: var(--v-font-body);
  font-size: 13px;
  color: var(--v-fg);
  margin: 0;
`;

const AdminCommentInput = styled.textarea`
  width: 100%;
  padding: 0.65rem 0.85rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  color: var(--v-fg);
  font-family: var(--v-font-body);
  font-size: 13px;
  outline: none;
  resize: vertical;
  min-height: 4rem;
  box-sizing: border-box;
  &:focus { border-color: var(--v-mint); }
  &::placeholder { color: var(--v-fg-muted); opacity: 0.4; }
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  flex-wrap: wrap;
`;

const SaveBtn = styled.button`
  background: ${({ $done }) => ($done ? "var(--v-mint)" : "var(--v-mint)")};
  border: none;
  color: #000;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.6rem 1.5rem;
  border-radius: 24px;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.85; }
  ${({ $loading }) => $loading && "opacity: 0.6; pointer-events: none;"}
  ${({ $error }) => $error && "background: #ff3b3b; color: #fff;"}
`;

const StatusGroup = styled.div`
  display: flex;
  gap: 6px;
`;

const StatusBtn = styled.button`
  width: 2.8rem;
  height: 2.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid ${({ $active, $color }) => ($active ? $color : "rgba(255,255,255,0.12)")};
  background: ${({ $active, $color }) => ($active ? `${$color}20` : "transparent")};
  color: ${({ $active, $color }) => ($active ? $color : "var(--v-fg-muted)")};
  font-size: 1rem;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${({ $color }) => $color};
    color: ${({ $color }) => $color};
    background: ${({ $color }) => `${$color}10`};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

RegistrationsItem.propTypes = {
  registration: PropTypes.shape({
    playerId: PropTypes.string,
    username: PropTypes.string,
    discordUsername: PropTypes.string,
    discordAvatarURL: PropTypes.string,
    oldUserId: PropTypes.string,
    email: PropTypes.string,
    aoe2ProfileId: PropTypes.string,
    tournamentHistory: PropTypes.arrayOf(PropTypes.shape({
      tournamentId: PropTypes.string,
      status: PropTypes.string,
      comments: PropTypes.string,
      registerConditions: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: PropTypes.bool })),
    })),
    clanId: PropTypes.number,
  }).isRequired,
  tournamentId: PropTypes.string.isRequired,
  clans: PropTypes.arrayOf(PropTypes.shape({ clanId: PropTypes.number.isRequired, name: PropTypes.string.isRequired, image: PropTypes.string.isRequired })).isRequired,
  clansInProgress: PropTypes.bool.isRequired,
  clansError: PropTypes.bool.isRequired,
  tournaments: PropTypes.array.isRequired,
  loadingTournaments: PropTypes.bool.isRequired,
  errorTournaments: PropTypes.bool.isRequired,
};

export default RegistrationsItem;
